/**
 * scheduleRunHelpers.js
 *
 * Shared utilities for running a scheduled report locally (i.e. opening the
 * matching report page pre-filled with the schedule's filters + time range
 * and auto-triggering the report). Used by ScheduledReportsPage's
 * "Run Report Local" kebab action.
 *
 * Three concerns live here:
 *   - the stored filter JSON → page state, via filterTermsToState
 *     (utils/reportFilterTerms.js, shared with the schedule drawers).
 *   - intervalIdToRange(report_interval_id) — turn 1-8 into a {start,end}
 *     pair using the report pages' own TIME_RANGES.
 *   - resolveScheduleRoute(row) — map report_type_id to { path, subType }
 *     so the caller knows where to navigate and which dropdown value to
 *     pre-select on the destination page.
 */

import { TIME_RANGES, DEFAULT_RANGE_ID } from '../../../../utils/reportPageState.js';
import { normalizeTimeWindow } from '../../../../utils/reportTimeWindow.js';
import { filterTermsToState, storedTimeWindow } from '../../../../utils/reportFilterTerms.js';
import { resolveScheduleRoute, scheduleSupportsTimeRange, scheduleSupportsTimeWindow } from '../../../../utils/reportTypes.js';

export { resolveScheduleRoute, scheduleSupportsTimeRange, scheduleSupportsTimeWindow };




// ---------------------------------------------------------------------------
// Interval ID → { startDate, endDate }
// ---------------------------------------------------------------------------
// IDs 1–8 are the report pages' TIME_RANGES (utils/reportPageState.js), so a
// schedule runs locally over exactly the range its preset shows. Anything else
// runs as Yesterday, and rangeId says so: returning the raw id (0, Custom) would
// make the page label a Yesterday run "Custom".
export function intervalIdToRange(reportIntervalId) {
    const id = parseInt(reportIntervalId, 10);
    const preset = TIME_RANGES.find((r) => r.id === id)
        || TIME_RANGES.find((r) => r.id === DEFAULT_RANGE_ID);
    const [startDate, endDate] = preset.getRange();
    return { startDate, endDate, rangeId: preset.id };
}

// ---------------------------------------------------------------------------
// Report-type facts (route, sub-type, editable range / window) live in one
// registry, utils/reportTypes.js (audit B2). Re-exported here so existing
// imports keep working.
// ---------------------------------------------------------------------------

/**
 * The eight schedulable reporting intervals, id -> label.
 *
 * Single source of truth shared by intervalIdToRange() above and the edit drawer's
 * Time Range picker, so a schedule can never display one range and query another.
 *
 * Deliberately no Custom entry. Interval 0 is Custom, which a static date pair makes
 * meaningless for a recurring report — intervalIdToRange() treats anything outside
 * 1-8 as Yesterday, and the picker never offers it.
 */
export const REPORT_INTERVALS = [
    { id: 1, label: 'Today' },
    { id: 2, label: 'Yesterday' },
    { id: 3, label: 'This Week' },
    { id: 6, label: 'Last Week' },
    { id: 4, label: 'This Month' },
    { id: 7, label: 'Last Month' },
    { id: 5, label: 'This Quarter' },
    { id: 8, label: 'Last Quarter' },
];


/** Label for an interval id, for display outside the picker. */
export function intervalLabel(reportIntervalId) {
    const found = REPORT_INTERVALS.find((r) => r.id === parseInt(reportIntervalId, 10));
    return found ? found.label : '';
}


// ---------------------------------------------------------------------------
// Top-level: schedule row → router state for navigation
// ---------------------------------------------------------------------------
/**
 * Build the navigation payload for "Run Report Local".
 *
 * @param {object} row — a scheduled report row
 * @returns {{ path: string|null, state: object }}
 */
export function runStateFromSchedule(row) {
    if (!row) return { path: null, state: null };
    const { path, subType } = resolveScheduleRoute(row.report_type_id);
    if (!path) return { path: null, state: null };

    const filters = filterTermsToState(row.report_filter_terms);
    const { startDate, endDate, rangeId } = intervalIdToRange(row.report_interval_id);

    /* The time-of-day window rides report_filter_terms but is not a list filter, so
       filterTermsToState leaves it alone. Carried only for the types that honour it — the
       destination pages seed reportFilters.timeWindow from state.filters. Absent means
       the page's all-day default, matching schedules saved before windows existed. */
    if (scheduleSupportsTimeWindow(row.report_type_id)) {
        const win = storedTimeWindow(row.report_filter_terms);
        if (win) filters.timeWindow = normalizeTimeWindow(win);
    }

    return {
        path,
        state: {
            autoRun: true,
            filters,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            rangeId,
            subType,
            sourceScheduleId: row.report_id ?? null,
            sourceDescription: row.description || '',
        },
    };
}
